import { useState, useEffect, useMemo } from 'react';
import Loader from "../Shared/Loader/Loader";
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import CommonConfig from '../../utils/constant';
import '../../assets/css/ParentAttendanceView.css';
import { useCatchResponse } from '../../utils/useCatchResponse';
import APIConstant from '../../utils/pathConstants';
import api from "../../utils/apiClient";

const ParentAttendanceView = () => {
    const { catchResponse } = useCatchResponse();
    const [selectedDate, setSelectedDate] = useState(new Date);
    const [securityUserID, setSecurityUserID] = useState("");
    const [statsData, setStatsData] = useState("");
    const [academicStartDate, setAcademicStartDate] = useState(null);
    const [academicEndDate, setAcademicEndDate] = useState(null);
    const [studentWiseAttendanceList, setStudentWiseAttendanceList] = useState();
    const [overallData, setOverallData] = useState(null);
    const [showUpcoming, setShowUpcoming] = useState(true);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        getFinancialYearData(loginData);

    }, []);

    const getFinancialYearData = async (userData) => {
        // Student: use their own id + the sidebar-selected academic year (the
        // `selectedAcademicYear` cookie); fall back to the current year by date.
        if (userData?.UserRole === 'Student') {
            const sid = userData?.CreatedBy;
            const years = await CommonConfig.getFinancialYears(sid) || [];
            let fy = CommonConfig.getCookie('selectedAcademicYear') || '';
            if (!fy) {
                const now = new Date();
                const cur = years.find(y => y?.StartDate && y?.EndDate && new Date(y.StartDate) <= now && now <= new Date(y.EndDate));
                fy = cur?._id || years[0]?._id || '';
            }
            GetChildreenAttendanceList(userData?._id, selectedDate, fy);
            GetOverallAttendance(userData?._id, fy);
            await GetConfigurationData(sid, years, fy);
            return;
        }
        const childId = userData?.SelectedChildData?._id;
        const selectedaYear = userData?.SelectedAcademicYearList?.[childId];
        if (childId) {
            GetChildreenAttendanceList(childId, selectedDate, selectedaYear);
            GetOverallAttendance(childId, selectedaYear);
        }
        await GetConfigurationData(userData?.CreatedBy, userData?.SelectedChildData?.AcademicDataList, selectedaYear);
    }

    const GetConfigurationData = async (id, academicYearList, selectedYear) => {
        try {
            let data = {
                id: id,
            }
            await api.post(APIConstant.path.GetGeneralConfigurationDetail, data).then(async response => {
                let res = response;
                if (res.success) {
                    if (res?.data?.IsAcademicYearApply) {
                        const academicCalendar = academicYearList || [];

                        if (!academicCalendar.length) {
                            setAcademicStartDate(null);
                            setAcademicEndDate(null);
                            return;
                        }

                        const validRanges = academicCalendar.filter(
                            item => item?.StartDate && item.EndDate && item?._id === selectedYear
                        );

                        if (!validRanges.length) {
                            setAcademicStartDate(null);
                            setAcademicEndDate(null);
                            return;
                        }

                        const sortedCalendar = [...validRanges].sort(
                            (a, b) =>
                                new Date(a?.StartDate) - new Date(b?.StartDate)
                        );

                        const start = new Date(sortedCalendar[0]?.StartDate);
                        const end = new Date(sortedCalendar[0]?.EndDate);

                        setAcademicStartDate(start);
                        setAcademicEndDate(end);
                    }
                    Loader.hide();
                }
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    }

    const formatDateToYMD = (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    };

    const GetChildreenAttendanceList = async (id, date, financialId) => {
        try {
            let formattedDate = formatDateToYMD(date);
            let data = {
                StudentId: id,
                SelectedDate: formattedDate,
                FinancialYearId: financialId
            }
            Loader.show();
            await api.post(APIConstant.path.GetParentWiseAttendanceList, data).then(async response => {
                let res = response;
                if (res.success) {
                    setStudentWiseAttendanceList(res?.data);
                    const stats = {
                        present: res?.data?.dayLevelStats?.present || 0,
                        absent: res?.data?.dayLevelStats?.absent || 0,
                        late: res?.data?.dayLevelStats?.late || 0,
                        partial: res?.data?.dayLevelStats?.partial || 0,
                        leave: res?.data?.holidayDays || 0
                    };
                    setStatsData(stats);
                }
                Loader.hide();
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    }

    const GetOverallAttendance = async (id, financialId) => {
        try {
            const data = { StudentId: id, FinancialYearId: financialId };
            const res = await api.post(APIConstant.path.GetParentWiseAttendanceOverall, data);
            if (res?.success && res?.data) setOverallData(res.data);
        } catch (err) {
            console.log(err);
        }
    }

    const renderDateCell = (date) => {
        if (!studentWiseAttendanceList?.attendance) return date.day;

        const dayData = studentWiseAttendanceList?.attendance.find(a => {
            const d = new Date(a?.date);
            return (
                d.getDate() === date?.day &&
                d.getMonth() === date?.month &&
                d.getFullYear() === date?.year
            );
        });

        const statusColors = {
            P: "#4CAF50",
            A: "#f44336",
            L: "#d97706",
            H: "#FFC107",
            PARTIAL: "#FF9800"
        };

        const examDayColor = "#9C27B0";
        const eventDayColor = "#2196f3";

        if (!dayData) {
            return (
                <div style={{
                    width: "2rem",
                    height: "2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "auto"
                }}>
                    {date?.day}
                </div>
            );
        }

        const hasExam = dayData?.examDetails?.length > 0;

        const examTooltip = hasExam
            ? dayData.examDetails.map(exam => {
                const start = new Date(exam?.startTime);
                const end = new Date(exam?.endTime);

                const startTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                const endTime = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                return `📌 ${exam?.examTitle || "Exam"}
                        📚 ${exam?.subjectName || ""}
                        ⏰ ${startTime} - ${endTime}
                        📝 Marks: ${exam?.marks || "-"}`;
            }).join("\n\n")
            : "";

        const hasEvent = !!dayData?.eventName;
        const eventTooltip = hasEvent ? `📅 ${dayData.eventName}${dayData?.eventRemark ? `\n${dayData.eventRemark}` : ''}` : '';

        const attType = dayData?.attendanceType == 'P' ? 'Present' : dayData?.attendanceType == 'A' ? 'Absent' : dayData?.attendanceType == 'L' ? 'Late' : dayData?.attendanceType == 'PARTIAL' ? 'Partial' : '-'

        const attendanceTooltip =
            dayData?.attendanceType === "H"
                ? `${dayData?.holidayName || "Holiday"}\n${dayData?.holidayRemark || ""}`
                : dayData?.subjectDetails?.length ? [
                    ...(dayData?.isSubjectWise && dayData?.summary ? [dayData.summary] : []),
                    ...dayData.subjectDetails.map(subject => {
                        const sType = subject?.attendanceType == 'P' ? 'Present' : subject?.attendanceType == 'A' ? 'Absent' : subject?.attendanceType == 'L' ? 'Late' : (attType || '-');
                        const time = subject?.classTime && subject?.classTime !== '--' ? ` (${subject?.classTime})` : '';
                        return `${subject?.subjectName}${time} - ${sType}`;
                    })
                ].join("\n") : attType;

        const tooltipContent = [examTooltip, eventTooltip, attendanceTooltip]
            .filter(Boolean)
            .join("\n\n")
            .trim() || attendanceTooltip;

        const hasAttendance =
            dayData?.attendanceType &&
            dayData?.attendanceType !== "-" &&
            dayData?.attendanceType !== "";

        const textColor =
            (hasExam || hasEvent) && !hasAttendance
                ? "white"
                : (dayData?.attendanceType === "L" || dayData?.attendanceType === "-")
                    ? "black"
                    : "white";

        const tooltipClass = `date-cell-${date?.year}-${date?.month}-${date?.day}`;

        let circleColor = "transparent";

        if (hasExam && !hasAttendance) {
            circleColor = examDayColor;
        } else if (hasAttendance) {
            circleColor = statusColors[dayData.attendanceType];
        } else if (hasEvent) {
            circleColor = eventDayColor;
        }

        return (
            <>
                <Tooltip target={`.${tooltipClass}`} content={tooltipContent} />
                <div
                    className={tooltipClass}
                    style={{
                        position: "relative",
                        width: "2rem",
                        height: "2rem",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                    }}
                >

                    {/* Exam + Attendance → Purple Ring */}
                    {hasExam && hasAttendance && (
                        <div
                            style={{
                                position: "absolute",
                                width: "2rem",
                                height: "2rem",
                                borderRadius: "50%",
                                border: `4px solid ${examDayColor}`,
                                boxSizing: "border-box"
                            }}
                        />
                    )}
                    {hasExam && hasAttendance && (
                        <div
                            style={{
                                position: "absolute",
                                width: "1.7rem",
                                height: "1.7rem",
                                borderRadius: "50%",
                                backgroundColor: "white"
                            }}
                        />
                    )}
                    <div
                        style={{
                            position: "absolute",
                            width: hasExam && hasAttendance ? "1.4rem" : "2rem",
                            height: hasExam && hasAttendance ? "1.4rem" : "2rem",
                            borderRadius: "50%",
                            backgroundColor: circleColor,
                            color: textColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "600"
                        }}
                    >
                        {date.day}
                    </div>
                </div>
            </>
        );
    };

    const handleMonthChange = (e) => {
        const newDate = new Date(e.value.getFullYear(), e.value.getMonth(), 1);
        handleCardMonthChange(newDate);
    };

    const handleCardMonthChange = async (value) => {
        try {
            setSelectedDate(value);
            const loginData = CommonConfig.loginData();
            // Student: own id + sidebar-selected year (fallback current by date).
            if (loginData?.UserRole === 'Student') {
                const sid = loginData?.CreatedBy;
                let fy = CommonConfig.getCookie('selectedAcademicYear') || '';
                if (!fy) {
                    const years = await CommonConfig.getFinancialYears(sid) || [];
                    const now = new Date();
                    const cur = years.find(y => y?.StartDate && y?.EndDate && new Date(y.StartDate) <= now && now <= new Date(y.EndDate));
                    fy = cur?._id || years[0]?._id || '';
                }
                await GetChildreenAttendanceList(loginData?._id, value, fy);
                return;
            }
            const childId = loginData?.SelectedChildData?._id;
            const selectedaYear = loginData?.SelectedAcademicYearList[childId];
            if (childId) {
                await GetChildreenAttendanceList(childId, value, selectedaYear);
            }
        } catch (err) {
            console.error('Error handling month change:', err);
            catchResponse(err);
        }
    }

    const events = useMemo(() => {
        if (!studentWiseAttendanceList?.attendance) return [];
        const list = [];
        for (const a of studentWiseAttendanceList.attendance) {
            if (!a?.date) continue;
            const date = new Date(a.date);

            if (a.attendanceType === 'H' && a.holidayName) {
                list.push({ title: a.holidayName, date, type: 'Holiday', icon: 'pi pi-star', color: '#FFC107', remark: a.holidayRemark || '' });
            }

            if (a.eventName) {
                list.push({ title: a.eventName, date, type: 'Event', icon: 'pi pi-calendar', color: '#2196f3', remark: a.eventRemark || '' });
            }

            if (a.examDetails?.length > 0) {
                for (const exam of a.examDetails) {
                    const start = exam.startTime ? new Date(exam.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                    const end = exam.endTime ? new Date(exam.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                    list.push({
                        title: `${exam.examTitle || 'Exam'} - ${exam.subjectName || ''}`,
                        date, type: 'Exam', icon: 'pi pi-book', color: '#9C27B0',
                        remark: `${start && end ? `${start} - ${end} | ` : ''}Marks: ${exam.marks || '-'}`,
                    });
                }
            }

            if (a.attendanceType && a.attendanceType !== '-' && a.attendanceType !== 'H') {
                const typeLabel = a.attendanceType === 'P' ? 'Present' : a.attendanceType === 'A' ? 'Absent' : a.attendanceType === 'L' ? 'Late' : a.attendanceType;
                const attColor = a.attendanceType === 'P' ? '#4CAF50' : a.attendanceType === 'A' ? '#f44336' : a.attendanceType === 'L' ? '#d97706' : '#FF9800';
                const attIcon = a.attendanceType === 'P' ? 'pi pi-check-circle' : a.attendanceType === 'A' ? 'pi pi-times-circle' : 'pi pi-clock';

                if (a.subjectDetails?.length > 0) {
                    if (a.isSubjectWise && a.summary) {
                        const allPresent = a.presentCount === a.totalSubjects;
                        list.push({
                            title: a.summary, date, type: 'Attendance',
                            icon: allPresent ? 'pi pi-check-circle' : 'pi pi-info-circle',
                            color: allPresent ? '#4CAF50' : '#FF9800',
                            remark: allPresent ? 'All subjects' : 'Subject-wise',
                        });
                    }
                    for (const s of a.subjectDetails) {
                        const sType = s.attendanceType || a.attendanceType;
                        const sLabel = sType === 'P' ? 'Present' : sType === 'A' ? 'Absent' : sType === 'L' ? 'Late' : sType;
                        const sColor = sType === 'P' ? '#4CAF50' : sType === 'A' ? '#f44336' : sType === 'L' ? '#d97706' : '#FF9800';
                        const sIcon = sType === 'P' ? 'pi pi-check-circle' : sType === 'A' ? 'pi pi-times-circle' : 'pi pi-clock';
                        list.push({
                            title: `${s.subjectName || 'Subject'}${s.classTime && s.classTime !== '--' ? ` (${s.classTime})` : ''}`,
                            date, type: 'Attendance', icon: sIcon, color: sColor, remark: sLabel,
                        });
                    }
                } else {
                    list.push({ title: `Attendance - ${typeLabel}`, date, type: 'Attendance', icon: attIcon, color: attColor, remark: typeLabel });
                }
            }
        }
        return list;
    }, [studentWiseAttendanceList]);

    const filteredEvents = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        return events.filter(e => e.type !== 'Attendance').filter(e => {
            const d = new Date(e.date); d.setHours(0, 0, 0, 0);
            return showUpcoming ? d >= today : d < today;
        }).sort((a, b) => showUpcoming ? a.date - b.date : b.date - a.date);
    }, [events, showUpcoming]);

    const formatEventDate = (d) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    const workingDays = studentWiseAttendanceList?.workingDays ?? 0;
    const swStats = studentWiseAttendanceList?.subjectWiseStats || { present: 0, absent: 0, late: 0, total: 0 };
    const fdStats = studentWiseAttendanceList?.fullDayStats || { present: 0, absent: 0, late: 0, total: 0 };
    const markedDayCount = (statsData?.present || 0) + (statsData?.absent || 0) + (statsData?.late || 0) + (statsData?.partial || 0);
    const notMarkedDays = Math.max(0, workingDays - markedDayCount);
    const attendedDays = (statsData?.present || 0) + (statsData?.late || 0);
    const attendancePct = workingDays ? Math.round((attendedDays / workingDays) * 100) : 0;

    return (
        <div className="wayment-owner-dashboard et-master-page">
            {CommonConfig.isEmpty(studentWiseAttendanceList) ?
                <div className="heading-area p-d-flex p-ai-center p-jc-between">
                    <div className="p-d-flex p-ai-baseline">
                        <i className="pi pi-calendar"></i>
                        <h2>Attendance View</h2>
                    </div>
                </div>
                :
                <>
                    <div className="heading-area p-d-flex p-ai-center p-jc-between">
                        <div className="p-d-flex p-ai-baseline">
                            <i className="pi pi-calendar"></i>
                            <h2>{studentWiseAttendanceList?.monthYear}</h2>
                        </div>
                    </div>
                    <div className='p-d-flex p-ai-center p-jc-around'>
                        <p style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1c515e',
                            marginVertical: '0.5rem'
                        }}>
                            Calendar
                        </p>
                        <p style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1c515e',
                            marginVertical: '0.5rem'
                        }}>
                            Attendance Summary
                        </p>
                    </div>
                    <div style={{
                        padding: '1.5rem',
                        background: '#fff',
                        paddingTop: '0',
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '2rem',
                        }}>
                            {/* Calendar Section */}
                            <div style={{
                                background: '#f9fafb',
                                borderRadius: '0.5rem',
                                border: '1px solid #e5e7eb',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                            }}>
                                <div className="calendar-section">
                                    <Calendar
                                        value={selectedDate}
                                        minDate={academicStartDate}
                                        maxDate={academicEndDate}
                                        onChange={handleMonthChange}
                                        inline
                                        dateTemplate={renderDateCell}
                                        className="attendance-calendar"
                                        onViewDateChange={handleMonthChange}
                                        showWeek={false}
                                    />
                                </div>
                            </div>

                            {/* Stats Section */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem'
                            }}>
                                {overallData && (
                                    <div style={{ background: '#f0f7ff', border: '1px solid #bfd8f5', borderRadius: '0.75rem', padding: '1.25rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1c515e' }}>Overall Attendance (Academic Year)</span>
                                            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#16a34a' }}>{overallData?.percentage || 0}%</span>
                                        </div>
                                        <div style={{ height: 8, background: '#e2ecf7', borderRadius: 999, overflow: 'hidden' }}>
                                            <div style={{ width: `${overallData?.percentage || 0}%`, height: '100%', background: '#16a34a', borderRadius: 999 }} />
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.4rem' }}>{overallData?.attendedDays || 0} attended (present + late) of {overallData?.workingDays || 0} working days</div>
                                        {((overallData?.subjectWiseStats?.total || 0) > 0 || (overallData?.fullDayStats?.total || 0) > 0) && (
                                            <div style={{ marginTop: '0.6rem', borderTop: '1px solid #dbe8f7', paddingTop: '0.5rem' }}>
                                                {(overallData?.subjectWiseStats?.total || 0) > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0' }}>
                                                        <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>Subject-wise</span>
                                                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                                            <span style={{ color: '#16a34a', fontWeight: 700 }}>{overallData?.subjectWiseStats?.present || 0}</span> P&nbsp;·&nbsp;
                                                            <span style={{ color: '#dc2626', fontWeight: 700 }}>{overallData?.subjectWiseStats?.absent || 0}</span> A&nbsp;·&nbsp;
                                                            <span style={{ color: '#d97706', fontWeight: 700 }}>{overallData?.subjectWiseStats?.late || 0}</span> L&nbsp;&nbsp;/&nbsp;{overallData?.subjectWiseStats?.total || 0} classes
                                                        </span>
                                                    </div>
                                                )}
                                                {(overallData?.fullDayStats?.total || 0) > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0' }}>
                                                        <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>Full Day</span>
                                                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                                            <span style={{ color: '#16a34a', fontWeight: 700 }}>{overallData?.fullDayStats?.present || 0}</span> P&nbsp;·&nbsp;
                                                            <span style={{ color: '#dc2626', fontWeight: 700 }}>{overallData?.fullDayStats?.absent || 0}</span> A&nbsp;·&nbsp;
                                                            <span style={{ color: '#d97706', fontWeight: 700 }}>{overallData?.fullDayStats?.late || 0}</span> L&nbsp;&nbsp;/&nbsp;{overallData?.fullDayStats?.total || 0} days
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#64748b' }}>This Month</span>
                                        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#16a34a' }}>{attendancePct}%</span>
                                    </div>
                                    <div style={{ height: 8, background: '#eef2f7', borderRadius: 999, overflow: 'hidden' }}>
                                        <div style={{ width: `${attendancePct}%`, height: '100%', background: '#16a34a', borderRadius: 999 }} />
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.4rem' }}>{attendedDays} attended (present + late) of {workingDays} working days</div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                    {[
                                        { label: 'Present', value: statsData?.present || 0, color: '#16a34a', bg: '#f0fdf4', unit: `of ${workingDays} days` },
                                        { label: 'Absent', value: statsData?.absent || 0, color: '#dc2626', bg: '#fef2f2', unit: `of ${workingDays} days` },
                                        { label: 'Late', value: statsData?.late || 0, color: '#d97706', bg: '#fff7ed', unit: `of ${workingDays} days` },
                                        { label: 'Partial', value: statsData?.partial || 0, color: '#FF9800', bg: '#fff7ed', unit: `of ${workingDays} days` },
                                        { label: 'Not Marked', value: notMarkedDays, color: '#64748b', bg: '#f1f5f9', unit: `of ${workingDays} days` },
                                        { label: 'Holiday', value: statsData?.leave || 0, color: '#b7791f', bg: '#fffbeb', unit: `of ${studentWiseAttendanceList?.totalDays} days` },
                                    ].map((s) => (
                                        <div key={s.label} style={{ background: s.bg, borderRadius: '0.5rem', padding: '0.9rem 1rem' }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>{s.label}</div>
                                            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color, lineHeight: 1.15 }}>{s.value}</div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.unit}</div>
                                        </div>
                                    ))}
                                </div>

                                {(swStats.total > 0 || fdStats.total > 0) && (
                                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '0.9rem 1.25rem' }}>
                                        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1c515e', marginBottom: '0.5rem' }}>Breakdown</div>
                                        {swStats.total > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: fdStats.total > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                                <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>Subject-wise</span>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                                    <span style={{ color: '#16a34a', fontWeight: 700 }}>{swStats.present}</span> P&nbsp;·&nbsp;
                                                    <span style={{ color: '#dc2626', fontWeight: 700 }}>{swStats.absent}</span> A&nbsp;·&nbsp;
                                                    <span style={{ color: '#d97706', fontWeight: 700 }}>{swStats.late}</span> L&nbsp;&nbsp;/&nbsp;{swStats.total} classes
                                                </span>
                                            </div>
                                        )}
                                        {fdStats.total > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0' }}>
                                                <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600 }}>Full Day</span>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                                    <span style={{ color: '#16a34a', fontWeight: 700 }}>{fdStats.present}</span> P&nbsp;·&nbsp;
                                                    <span style={{ color: '#dc2626', fontWeight: 700 }}>{fdStats.absent}</span> A&nbsp;·&nbsp;
                                                    <span style={{ color: '#d97706', fontWeight: 700 }}>{fdStats.late}</span> L&nbsp;&nbsp;/&nbsp;{fdStats.total} days
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Event Listing Section */}
                    <div style={{ padding: '0 1.5rem 1.5rem' }}>
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1c515e' }}>Agenda</h3>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setShowUpcoming(true)}
                                        style={{
                                            padding: '6px 16px', borderRadius: '16px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                            background: showUpcoming ? '#4CAF50' : '#f3f4f6', color: showUpcoming ? '#fff' : '#666',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >Upcoming</button>
                                    <button
                                        onClick={() => setShowUpcoming(false)}
                                        style={{
                                            padding: '6px 16px', borderRadius: '16px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                            background: !showUpcoming ? '#f44336' : '#f3f4f6', color: !showUpcoming ? '#fff' : '#666',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >Past</button>
                                </div>
                            </div>

                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {filteredEvents.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                                        <i className={showUpcoming ? 'pi pi-calendar-plus' : 'pi pi-calendar-minus'} style={{ fontSize: '2rem', marginBottom: '8px', display: 'block', opacity: 0.4 }}></i>
                                        {showUpcoming ? 'No upcoming events' : 'No past events'}
                                    </div>
                                ) : (
                                    filteredEvents.map((event, idx) => {
                                        const d = event.date;
                                        const today = new Date(); today.setHours(0, 0, 0, 0);
                                        const eventDay = new Date(d); eventDay.setHours(0, 0, 0, 0);
                                        const isUpcoming = eventDay >= today;
                                        const statusColor = isUpcoming ? '#4CAF50' : '#f44336';

                                        return (
                                            <div key={idx} style={{
                                                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', marginBottom: '8px',
                                                background: '#fff', borderRadius: '10px', borderLeft: `4px solid ${event.color}`,
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'transform 0.15s ease',
                                            }}
                                                onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                                                onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
                                            >
                                                {/* Date box */}
                                                <div style={{
                                                    width: '44px', minWidth: '44px', height: '44px', borderRadius: '10px',
                                                    background: `${event.color}15`, display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 800, color: event.color, lineHeight: 1 }}>{d.getDate()}</span>
                                                    <span style={{ fontSize: '9px', fontWeight: 600, color: event.color }}>
                                                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}
                                                    </span>
                                                </div>

                                                {/* Content */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                                                        <i className={event.icon} style={{ fontSize: '12px', color: event.color }}></i>
                                                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1c515e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {event.title}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#999' }}>{formatEventDate(d)}</div>
                                                    {event.remark && (
                                                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {event.remark}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Badges */}
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: 700,
                                                        background: `${event.color}15`, color: event.color,
                                                    }}>{event.type}</span>
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: 700,
                                                        background: `${statusColor}15`, color: statusColor,
                                                    }}>{isUpcoming ? 'Upcoming' : 'Past'}</span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </>}
        </div>
    );
};

export default ParentAttendanceView;